import React, { useState, useEffect } from "react";
import {
  UploadCloud,
  FileIcon,
  X,
  AlertTriangle,
  CheckCircle,
  Upload,
  Settings,
  Check,
} from "lucide-react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import { bulkUpload } from "../utils/Api";

interface FileEntry {
  file: File;
  name: string;
}

interface BulkUploadModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const SUPPORTED_EXTENSIONS = [
  // PDF
  ".pdf",
  // Images
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".bmp",
  ".tiff",
  ".tif",
  ".svg",
  // Word
  ".doc",
  ".docx",
  ".odt",
  ".rtf",
  // Excel
  ".xls",
  ".xlsx",
  ".ods",
  ".csv",
  // PowerPoint
  ".ppt",
  ".pptx",
  ".odp",
  // Text
  ".txt",
  ".md",
  // Other
  ".xml",
  ".json",
];

function isFileSupported(file: File): boolean {
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
  return SUPPORTED_EXTENSIONS.includes(ext);
}

function nameFromFile(file: File): string {
  const lastDot = file.name.lastIndexOf(".");
  return lastDot > 0 ? file.name.slice(0, lastDot) : file.name;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  setIsOpen,
}) => {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEntries([]);
      setError(null);
      setIsSubmitting(false);
      setProgress(0);
      setSuccess(false);
    }
  }, [isOpen]);

  const addFiles = (files: File[]) => {
    const supported = files.filter(isFileSupported);
    const unsupported = files.filter((f) => !isFileSupported(f));

    if (unsupported.length > 0) {
      setError(
        `Unsupported file type(s): ${unsupported.map((f) => f.name).join(", ")}`,
      );
    } else {
      setError(null);
    }

    if (supported.length > 0) {
      setEntries((prev) => [
        ...prev,
        ...supported.map((file) => ({ file, name: nameFromFile(file) })),
      ]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
      e.target.value = "";
    }
  };

  const handleNameChange = (index: number, value: string) => {
    setEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, name: value } : entry)),
    );
  };

  const handleRemove = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emptyName = entries.find((entry) => !entry.name.trim());
    if (emptyName) {
      setError("All documents must have a name.");
      return;
    }

    setIsSubmitting(true);
    setProgress(0);
    setError(null);

    try {
      await bulkUpload(entries, (e) => {
        const pct =
          e.total && e.total > 0
            ? Math.round((e.loaded / e.total) * 100)
            : e.progress != null
              ? Math.round(e.progress * 100)
              : 0;
        if (pct > 0) setProgress(pct);
      });
      setProgress(100);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail || "Upload failed. Please try again.",
      );
      setIsSubmitting(false);
      setProgress(0);
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      {success ? (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <CheckCircle size={40} className="text-green-500" />
          <div>
            <p className="text-base font-semibold tracking-tight text-gray-900">
              Documents received!
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Our team will process your documents and return them to your
              account. You'll get a confirmation email shortly.
            </p>
          </div>
          <PrimaryButton onClick={() => setIsOpen(false)} className="mt-2 w-full">
            Done
          </PrimaryButton>
        </div>
      ) : (
        <>
          <h1 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
            Submit documents for processing
          </h1>
          <p className="text-sm text-gray-500 mb-5">
            Upload your documents and our team will process and return them to
            your account.
          </p>

          {/* Steps */}
          <div className="flex items-start justify-between gap-2 border border-gray-200 rounded-lg px-4 py-4 mb-5">
            <div className="flex flex-col items-center gap-1.5 flex-1 text-center">
              <div className="w-7 h-7 rounded-full bg-[#1d365d] flex items-center justify-center">
                <Upload size={13} className="text-white" />
              </div>
              <p className="text-xs font-medium text-gray-700">Upload</p>
              <p className="text-xs text-gray-400 leading-tight">Send us your files</p>
            </div>
            <div className="text-gray-300 mt-2.5">›</div>
            <div className="flex flex-col items-center gap-1.5 flex-1 text-center">
              <div className="w-7 h-7 rounded-full bg-[#1d365d] flex items-center justify-center">
                <Settings size={13} className="text-white" />
              </div>
              <p className="text-xs font-medium text-gray-700">We process</p>
              <p className="text-xs text-gray-400 leading-tight">Our team handles everything</p>
            </div>
            <div className="text-gray-300 mt-2.5">›</div>
            <div className="flex flex-col items-center gap-1.5 flex-1 text-center">
              <div className="w-7 h-7 rounded-full bg-[#1d365d] flex items-center justify-center">
                <Check size={13} className="text-white" />
              </div>
              <p className="text-xs font-medium text-gray-700">Ready in your account</p>
              <p className="text-xs text-gray-400 leading-tight">Processed docs appear here</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Drop zone */}
              <div
                className="bg-gray-50/50 border border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-[#1d365d]/40 transition-colors"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <UploadCloud className="text-[#1d365d] mx-auto mb-3" size={36} />
                <label
                  htmlFor="bulk-file-upload"
                  className="text-sm font-medium text-[#1d365d] cursor-pointer hover:text-[#2d4a75] transition-colors"
                >
                  Choose files
                </label>
                <span className="text-sm text-gray-400"> or drag and drop</span>
                <input
                  id="bulk-file-upload"
                  type="file"
                  multiple
                  className="sr-only"
                  onChange={handleFileChange}
                />
                <p className="text-xs text-gray-400 mt-2">
                  PDF, Word, Excel, PowerPoint, images, CSV, text, and more
                </p>
              </div>

              {/* File list */}
              {entries.length > 0 && (
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  <p className="text-[10px] font-medium uppercase tracking-widest text-gray-400">
                    {entries.length} file{entries.length !== 1 ? "s" : ""} — edit names below
                  </p>
                  {entries.map((entry, index) => (
                    <div
                      key={`${entry.file.name}-${index}`}
                      className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2"
                    >
                      <FileIcon size={14} className="text-gray-400 flex-shrink-0" />
                      <input
                        type="text"
                        value={entry.name}
                        onChange={(e) => handleNameChange(index, e.target.value)}
                        required
                        className="flex-1 min-w-0 text-sm text-gray-900 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-[#1d365d] focus:outline-none py-0.5 transition-colors"
                        placeholder="Document name"
                      />
                      <span className="text-xs text-gray-400 flex-shrink-0 truncate max-w-24 hidden sm:block">
                        {entry.file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
                  <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {isSubmitting && (
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1.5">
                    <span>{progress < 100 ? "Uploading…" : "Processing…"}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-[#1d365d] h-1.5 rounded-full transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              <PrimaryButton
                type="submit"
                disabled={entries.length === 0 || isSubmitting}
                className="flex items-center justify-center gap-2 w-full"
              >
                {`Upload ${entries.length > 0 ? `${entries.length} document${entries.length !== 1 ? "s" : ""}` : "documents"}`}
              </PrimaryButton>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default BulkUploadModal;
